import React, { useRef, useMemo, useState, useEffect } from "react";

import styled, { css } from "styled-components";

import { debounce, hasErrorObject } from "utils";
import { api } from "api/loginRoutes";
import { SCREEN_MEDIA } from "const";
import {
    SMS_TRAFFIC_LIMITS_CREATE_API
} from "const/apiMethods";
import CustomField from "components/Form/CustomField";
import Modal from "components/Modal/Modal";
import Table from "components/Table/Table";

import { FlexboxGrid, Form, Input, InputNumber, Radio, RadioGroup, Schema, SelectPicker } from "rsuite";

const { StringType, NumberType } = Schema.Types;

const defaultFormValue = { senderid: "", day: null, hour: null, mcc: null, mnc: null };


export default (
    {
        show,
        transitTrunkList,
        suppliersGroupList,
        supplierId,
        onSuccess,
        onClose,
        disabled = false,
        trafficTypeState,
        e212List,
        countryList,
        service,
        selectedDefaultData,

        pickTrunk,
        getE212DropdownList,
        setSelectedDefaultData,
        getReferencesForReports,
        getSupplierPrefixesDropdown
    }
) => {

    const isGanGroup = trafficTypeState === "gan_group";
    const isGanNumber = trafficTypeState === "gan";
    const isPrefix = trafficTypeState === "prefix";
    const isMcc = trafficTypeState === "mcc";
    const isDefault = trafficTypeState === "mcc_default";

    const ganGroupModel = Schema.Model({
        senderid: StringType()
            .maxLength(40, "The maximum is 40 characters"),
        // .isRequired("This field is required"),
        gan_group_id: StringType()
            .isRequired("This field is required"),
        day: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
        hour: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
    });

    const ganNumberModel = Schema.Model({
        senderid: StringType()
            .maxLength(40, "The maximum is 40 characters"),
        // .isRequired("This field is required"),
        number: StringType()
            .pattern(/^[0-9][\d]*$/, "The number must contain only digits")
            .minLength(3, "The minimum is 3 characters.")
            .maxLength(15, "The maximum is 15 characters")
            .isRequired("This field is required"),
        day: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
        hour: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
    });

    const prefixModel = Schema.Model({
        senderid: StringType()
            .maxLength(40, "The maximum is 40 characters"),
        // .isRequired("This field is required"),
        // trunk_id: StringType()
        //     .isRequired("This field is required"),
        prefix: StringType()
            .pattern(/^[0-9][\d]*$/, "The prefix must contain only digits")
            .minLength(3, "The minimum is 3 characters.")
            .maxLength(15, "The maximum is 15 characters")
            .isRequired("This field is required"),
        day: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
        hour: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
    });

    const mccModel = Schema.Model({
        mcc: StringType()
            .isRequired("This field is required"),
        mnc: StringType(),
        senderid: StringType()
            .maxLength(40, "The maximum is 40 characters"),
        day: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
        hour: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
        hour_night: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required"),
        trunk_id: StringType()
            .isRequired("This field is required")
    });

    const operatorFilterModel = Schema.Model({
        code: StringType()
            .pattern(/^\d*$/, "Only numbers")
            .maxLength(15, "The maximum is only 15 characters.")
    });

    const defaultOperatorFilter = {
        mcc: "",
        mnc: "",
        country: null,
        operator: ""
    };

    const formRef = useRef(null);
    const [_disabled, setDisabled] = useState(disabled);

    const [activeOperator, setActiveOperator] = useState(null);
    const [activeOperatorData, setActiveOperatorData] = useState({});

    const [formValue, setFormValue] = useState(defaultFormValue);
    const [defaultLimitType, setDefaultLimitType] = useState("supplier");

    const [operatorFilter, setOperatorFilter] = useState(defaultOperatorFilter);

    const method = SMS_TRAFFIC_LIMITS_CREATE_API;

    const [transitTrunkId, setTransitTrunkId] = useState(null);

    const defaultModel = useMemo(() => {
        return Schema.Model({
            senderid: StringType()
                .maxLength(40, "The maximum is 40 characters"),
            // .isRequired("This field is required"),
            day: NumberType()
                .min(1, "Minimum 1")
                .max(10000000, "Maximum 10000000")
                .isRequired("This field is required"),
            hour: NumberType()
                .min(1, "Minimum 1")
                .max(10000000, "Maximum 10000000")
                .isRequired("This field is required"),
            ...(defaultLimitType === "mcc_default" ? {
                hour_night: NumberType()
                    .min(1, "Minimum 1")
                    .max(10000000, "Maximum 10000000")
                    .isRequired("This field is required")
            } : {}),
            ...(defaultLimitType === "mcc_default" ? {
                trunk_id: StringType()
                    .isRequired("This field is required")
            } : {})
        });
    }, [defaultLimitType]);

    const formCreateModels = {
        gan_group: ganGroupModel,
        gan: ganNumberModel,
        prefix: prefixModel,
        mcc: mccModel,
        mcc_default: defaultModel
    };

    const formCreateHasError = useMemo(() => {
        const fullFormValue = isMcc ? { ...formValue, ...activeOperatorData } : formValue;
        const formCreateErrorCheck = trafficTypeState in formCreateModels && formCreateModels[trafficTypeState].check(fullFormValue || {});
        return Object.keys(formCreateErrorCheck).some(field => formCreateErrorCheck[field]?.hasError);
    }, [formValue, activeOperatorData, trafficTypeState, defaultLimitType]);

    useEffect(() => {
        getReferencesForReports();
    }, []);

    useEffect(() => {
        if (Object.keys(selectedDefaultData).some((key) => !!selectedDefaultData[key])) {
            setFormValue(selectedDefaultData);
        } else {
            setFormValue(defaultFormValue);
        }
    }, [selectedDefaultData]);

    useEffect(() => {
        if (!service) {
            getE212DropdownList(operatorFilter, true);
        }
    }, [operatorFilter]);

    useEffect(() => {
        if (transitTrunkId) {
            getSupplierPrefixesDropdown(transitTrunkId);
        }
    }, [transitTrunkId]);

    useEffect(() => {
        if (formRef.current) {
            formRef.current.check();
        }
    }, [defaultLimitType]);

    const clearOperatorFilter = () => {
        setOperatorFilter(defaultOperatorFilter);
    };

    const onSubmit = async () => {
        const {
            hour: hour, day: day, hour_night: hour_night, gan_group_id, number, prefix, mcc, mnc, senderid,
            trunk_id: supplierTrunkId,
            ...formValueLast
        } = formValue;

        const formGanNumber = [number];
        const formPrefixNumber = [prefix];

        const keyParams = {
            "sms.supplier_id": supplierId
        };

        const params = {
            target: {
                ...(isGanGroup ? { "sms.supplier_gan_group_id": gan_group_id } : {}),
                ...(isGanNumber ? { "sms.supplier_gan_number_list": formGanNumber } : {}),
                ...(isPrefix ? { "sms.supplier_prefix_prefix_list": formPrefixNumber } : {}),
                ...(isMcc ? { "mcc": activeOperatorData?.mcc, ...(activeOperatorData?.mnc ? { "mnc": activeOperatorData.mnc } : {}) } : {}),
                ...((isMcc || isDefault) && supplierTrunkId ? { "sms.supplier_trunk_id": supplierTrunkId } : {}),
                ...(mcc ? { mcc, ...(mnc ? { mnc } : {}) } : {}),
                ...keyParams,
            },
            senderid: senderid || "",
            hour: parseInt(hour),
            day: parseInt(day),
            ...(hour_night ? { hour_night: parseInt(hour_night) } : {}),
            type: isDefault ? defaultLimitType : trafficTypeState,
            ...formValueLast
        };

        setDisabled(true);

        const result = await api(method, params);

        if (!("error" in result)) {
            onSuccess();
            setSelectedDefaultData(defaultFormValue);
        }

        setDisabled(false);
    };

    const currentE212List = useMemo(() => {
        return e212List.map((item) => {
            return {
                ...item,
                countryOperatorName: `${item.country_name}, ${item.operator} (${item.mcc}${item.mnc})`,
                mccMnc: `${item.mcc}-${item.mnc}`
            }
        });
    }, [e212List]);

    const e212Columns = useMemo(() => [
        {
            id: "countryOperatorName",
            dataKey: "countryOperatorName",
            align: "left",
            label: "Operator",
            className: "countryOperatorName",
            // width: 140,
            flexGrow: 1
        }
    ], []);

    const modalTitle = useMemo(() => {
        if (selectedDefaultData.mcc) {
            return "Modify traffic limit";
        }
        return "Create traffic limit";
    }, [selectedDefaultData]);

    const daytimeLabel = (isMcc || isDefault && defaultLimitType !== "supplier") ? "Daytime Hour" : "Hour";

    return (
        <>
            {show && <Modal
                show={show}
                title={modalTitle}
                onClose={() => {
                    onClose();
                    setActiveOperator(null);
                    setActiveOperatorData({});
                    clearOperatorFilter();
                    setFormValue(defaultFormValue);
                    setSelectedDefaultData(defaultFormValue);
                    setDefaultLimitType("supplier");
                }}
                footer={true}
                width={680}
                successText="Create"
                disabled={_disabled}
                extraDisabled={formCreateHasError}
                onSuccess={() => {
                    onSubmit();
                    setActiveOperator(null);
                    setActiveOperatorData({});
                    clearOperatorFilter();
                    setFormValue(defaultFormValue);
                    setDefaultLimitType("supplier");
                }}
            >
                {isMcc && !selectedDefaultData.mcc && <Form
                    model={operatorFilterModel}
                    formDefaultValue={defaultOperatorFilter}
                    onChange={debounce((filterFormValue) => {
                        const formCheckData = operatorFilterModel.check(filterFormValue);
                        if (!hasErrorObject(formCheckData)) {
                            const pickedCountryCode = filterFormValue?.country;
                            const pickedCountryName = countryList.find((item) => item?.cntr_code === pickedCountryCode);
                            const currentFormValue = {
                                ...filterFormValue,
                                country: pickedCountryName?.name
                            };

                            setOperatorFilter(currentFormValue);
                        }
                    }, 1000)}
                >
                    <FilterFlexboxGrid align="middle">
                        <FlexboxGrid.Item>
                            <Form.Control
                                accepter={Input}
                                name="mcc"
                                placeholder="MCC"
                                style={{ width: 80 }}
                            />
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item>
                            <Form.Control
                                accepter={SelectPicker}
                                data={countryList}
                                labelKey="name"
                                valueKey="cntr_code"
                                placeholder="Country"
                                name="country"
                                style={{ width: 200 }}
                            />
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item>
                            <Form.Control
                                accepter={Input}
                                name="mnc"
                                placeholder="MNC"
                                style={{ width: 80 }}
                            />
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item>
                            <Form.Control
                                accepter={Input}
                                name="operator"
                                placeholder="Operator"
                                style={{ width: 200 }}
                            />
                        </FlexboxGrid.Item>
                    </FilterFlexboxGrid>
                </Form>}
                {isMcc && !selectedDefaultData.mcc && <Table
                    row_key="mccMnc"
                    active_id={activeOperator}
                    virtualized
                    shouldUpdateScroll={false}
                    data={currentE212List}
                    extraHeight={(currentE212List.length && currentE212List.length < 7) ? (currentE212List.length * 40) + 40 : 280}
                    columns={e212Columns}
                    rowHeight={40}

                    onRowClick={(value) => {
                        setActiveOperator(value);
                        const [mcc, mnc] = value.split("-");

                        setActiveOperatorData({
                            mcc,
                            mnc
                        });
                    }}
                />}
                <StyledForm
                    ref={formRef}
                    model={formCreateModels[trafficTypeState]}
                    formValue={formValue}
                    onChange={setFormValue}
                >
                    {!isMcc && <>
                        {isDefault && <RadioGroup
                            inline
                            name="limit_type"
                            defaultValue="supplier"
                            onChange={setDefaultLimitType}
                            value={defaultLimitType}
                        >
                            <Radio value="supplier">Whole account limit</Radio>
                            <Radio value="mcc_default">Default MCC limit</Radio>
                        </RadioGroup>}

                        {isGanGroup && <StyledField isMcc={isMcc}>
                            <Form.ControlLabel>Gan Groups</Form.ControlLabel>
                            <Form.Control
                                name="gan_group_id"
                                accepter={SelectPicker}
                                data={suppliersGroupList}
                                valueKey={"id"}
                                labelKey={"name"}
                                placeholder="Seletect gan group"
                            />
                        </StyledField>}

                        {isPrefix && <StyledField isMcc={isMcc}>
                            <Form.ControlLabel>Prefix</Form.ControlLabel>
                            <Form.Control
                                name="prefix"
                                accepter={Input}
                                placeholder="Input prefix"
                            />
                        </StyledField>}

                        {isGanNumber && <StyledField isMcc={isMcc}>
                            <Form.ControlLabel>Number</Form.ControlLabel>
                            <Form.Control
                                name="number"
                                accepter={Input}
                                placeholder="Input number"
                            />
                        </StyledField>}

                    </>}

                    {(isMcc || (isDefault && defaultLimitType === "mcc_default")) && <StrechedContainer>
                        <CustomField
                            accepter={SelectPicker}
                            data={transitTrunkList}
                            cleanable={false}
                            searchable={false}
                            valueKey="id"
                            labelKey="name"
                            placeholder="Select trunk"
                            errorPlacement="bottomLeft"
                            label="Trunk"
                            name="trunk_id"
                            // value={pickedSupplierPlan?.id}
                            onChange={(id) => {
                                const trunkValue = transitTrunkList.find(trunk => trunk?.id === id);

                                pickTrunk(trunkValue)
                            }}
                            style={{
                                width: "100%"
                            }}
                            styleLabelExternal={{
                                width: 160,
                                marginRight: 0,
                                flexShrink: 0
                            }}
                        />
                    </StrechedContainer>}

                    <StrechedContainer isMcc={isMcc}>
                        <CustomField
                            name="senderid"
                            label="Sender ID"
                            accepter={Input}
                            placeholder="Type senderid"
                            styleLabelExternal={{
                                width: 160,
                                marginRight: 0,
                                flexShrink: 0
                            }}
                        />
                    </StrechedContainer>

                    <StrechedContainer isMcc={isMcc}>
                        <CustomField
                            name="day"
                            label="Day"
                            accepter={InputNumber}
                            placeholder="Day"
                            styleLabelExternal={{
                                width: 160,
                                marginRight: 0,
                                flexShrink: 0
                            }}
                        />
                    </StrechedContainer>

                    <StrechedContainer isMcc={isMcc}>
                        <CustomField
                            name="hour"
                            label={daytimeLabel}
                            accepter={InputNumber}
                            placeholder={(isMcc || isDefault && defaultLimitType !== "supplier") ? "Daytime Hour" : "Hour"}
                            styleLabelExternal={{
                                width: 160,
                                marginRight: 0,
                                flexShrink: 0
                            }}
                        />
                    </StrechedContainer>

                    {isMcc || (isDefault && defaultLimitType !== "supplier") ? <StrechedContainer isMcc={isMcc}>
                        <CustomField
                            name="hour_night"
                            label="Nighttime Hour"
                            accepter={InputNumber}
                            // style={{ width: 200 }}
                            styleLabelExternal={{
                                width: 160,
                                marginRight: 0,
                                flexShrink: 0
                            }}
                            placeholder="Nighttime Hour"
                        />
                    </StrechedContainer> : <></>}

                </StyledForm>

            </Modal>
            }
        </>

    );
};

const FilterFlexboxGrid = styled(FlexboxGrid)`
    .rs-flex-box-grid-item {
        margin-right: 10px;
    }
`;

const StyledForm = styled(Form)`
    .rs-form-group {
        display: flex;
        align-items: center;
    }
    
    .rs-control-label {
        margin-right: 20px;
        margin-bottom: 0;
        width: 100px;
    }
    
    .rs-picker-select {
        width: 100%;
    }
`;

const StyledField = styled.div`
    padding-bottom: ${props => props.isMcc ? 5 : 10}px;
    padding-top: ${props => props.isMcc ? 5 : 10}px;

    @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
        display: flex;
    }

    .rs-control-label {
        flex-shrink: 0;
        align-self: center;
        padding-right: 10px;
        width: 100%;
        margin-right: 0;

        @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
            width: 160px;
        }
    }

    .rs-input-number {

        @media (min-width: ${SCREEN_MEDIA.sm.min}px) {
            max-width: 190px;
        }
    }

    ${props => props.isCheckbox && css`
        display: flex;
        
        @media (max-width: ${SCREEN_MEDIA.sm.max}px) {

            .rs-form-control-wrapper {
                width: auto;
            }

            .rs-control-label {
                width: auto;
                order: 1;
            }
        }
    `}
`;

const StrechedContainer = styled.div`
    && {
        width: 100%;
        margin-top: 10px;
        margin-bottom: 10px;
    }
`;